import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { useMsal, useAccount } from '@azure/msal-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { InteractionRequiredAuthError, BrowserAuthError, InteractionStatus } from '@azure/msal-browser'
import toast from 'react-hot-toast'

import { User } from '@/types'
import { loginRequest, silentRequest, autoLoginRequest } from '@/config/auth'
import { API_CONFIG } from '@/config/api'
import { signalTokenRefreshComplete, signalAuthTokenReady, decodeJwtExp } from '@/services/apiClient'
import { sessionManager, useIsSessionExpired } from '@/services/sessionManager'
import { SessionExpiredModal } from './SessionExpiredModal'
import { ProfileLoadingNote } from './ProfileLoadingNote'
import { useProfileRetry } from './profileRetry'
// Relative, not '@/services/...': hr-ops mounts this provider too, without those aliases.
import { authenticatedFetch } from '../api/authenticatedFetch'
import { PERSONA_REFUSED_EVENT } from '../api/personaRefusal'
import type { PersonaRefusal } from '../api/personaRefusal'

// Constants for token refresh management
const MAX_REFRESH_ATTEMPTS = 3
const REFRESH_COOLDOWN_MS = 5000 // 5 seconds between refresh attempts
// Bounded wait for MSAL initialize()/handleRedirectPromise before a refresh
// gives up. Must stay under the 15s the api clients wait for refresh results.
const MSAL_SETTLE_WAIT_MS = 8000
// If the boot auto-login redirect hasn't navigated away by then, it's stuck
// (blocked navigation, silent Entra failure) — surface the session modal.
const AUTO_LOGIN_WATCHDOG_MS = 10000

// One-shot guard for the boot-time auto-login redirect (sessionStorage = per
// tab session). Prevents a redirect loop if Entra keeps bouncing back without
// establishing a session; cleared once a user is successfully established.
const AUTO_LOGIN_GUARD_KEY = 'auth.autoLoginAttempted'

export interface TestPersona {
  id: string
  personnelNumber: string
  email: string
  fullName: string
  firstName: string
  lastName: string
  jobTitle: string
  department: string
  campus: string
  campusName: string
  functionalArea: string
  jobFamilyId?: string
  roles: string[]
  // Direct capability grants for a dev/UAT persona, mirroring `roles`. Sent as
  // X-Test-User-Permissions so a persona can be given e.g. budget:read with no role.
  permissions?: string[]
  positionId: string
  positionDescription: string
  managerPersonnelNumber?: string
  reportsToPositionId?: string
  isPrimary: boolean
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isSessionExpired: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  refetchUser: () => void
  // Development mode functions
  switchToTestPersona: (persona: TestPersona) => void
  clearTestPersona: () => void
  hasActivePersona: boolean
  isDevelopmentMode: boolean
  /** True while a persona's permission grants are still being fetched. Route
   *  guards must WAIT on this — deciding early bounces a granted user off the
   *  page their grant unlocks, because permissions are still []. */
  arePermissionsLoading: boolean
  /** The profile (and with it the roles) has not loaded: `user` is a role-less stand-in
   *  from the sign-in claims until it does. It is retried in the background. */
  isProfileIncomplete: boolean
  /** Ask for the profile again now. */
  retryProfile: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * Local sign-out hygiene — call BEFORE instance.logoutRedirect().
 *
 * 1. Drops the derived bearer: MSAL's logout clears ITS cache but knows
 *    nothing about our `access_token`. Leaving it produced a post-logout
 *    ghost state — no MSAL account (user === null, header userless) while
 *    API calls still carried a token valid until its exp.
 * 2. Arms the one-shot auto-login guard: the post-logout landing must REST on
 *    the login page. Without this, the boot-expiry auto-login treated the
 *    freshly signed-out state as "reopened tab with expired token" and
 *    redirected straight back to Entra — an intentional sign-out turned into
 *    a refresh loop. The guard re-arms automatically once a user signs in.
 */
export function prepareForLogout(): void {
  localStorage.removeItem('access_token')
  try {
    sessionStorage.setItem(AUTO_LOGIN_GUARD_KEY, String(Date.now()))
  } catch {
    // sessionStorage disabled — non-fatal
  }
  sessionManager.reset()
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal()
  const account = useAccount(accounts[0] || null)
  // MSAL lifecycle gate: until initialize()/handleRedirectPromise settle,
  // getAllAccounts() returns [] even when a cached account sits in
  // localStorage. Nothing may treat "no accounts" as session-expiry (or
  // start a login redirect) before this is true — doing so shipped a boot
  // death-spiral to PROD on 2026-07-14: fresh boots marked the session
  // expired mid-init, cleared the token, and wedged on the auto-login.
  const msalSettled = inProgress === InteractionStatus.None
  const msalSettledRef = useRef(msalSettled)
  msalSettledRef.current = msalSettled
  const navigate = useNavigate()
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  // Guards must not decide access until a persona's DB grants have arrived.
  const [arePermissionsLoading, setArePermissionsLoading] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [devPersona, setDevPersona] = useState<TestPersona | null>(null)
  const [isAcquiringToken, setIsAcquiringToken] = useState(false)
  const [personaLoaded, setPersonaLoaded] = useState(false)
  // The profile (/auth/me) carries the person's roles. When it fails to load they run on a
  // role-less stand-in built from the sign-in claims; this says so, and the retry below keeps
  // asking until it loads (Victor, 5 Oct 2026: an HRBP sat through two Annual I&G sessions
  // without his roles because one load failed and nothing tried again).
  const [profileIncomplete, setProfileIncomplete] = useState(false)
  // The last profile that did load: a later failed refresh keeps it rather than taking the
  // person's roles away mid-visit.
  const loadedProfileRef = useRef<User | null>(null)
  const profileFetchInFlightRef = useRef(false)

  // Token refresh management to prevent loops
  const refreshAttemptsRef = useRef(0)
  const lastRefreshTimeRef = useRef(0)
  // Single-flight: the in-flight refresh promise, shared with every caller
  // that asks while it runs (see attemptTokenRefresh)
  const refreshInFlightRef = useRef<Promise<string | null> | null>(null)
  const hasRedirectedRef = useRef(false)
  const userRef = useRef<User | null>(null)
  
  // Session expired state from global manager
  const isSessionExpired = useIsSessionExpired()

  // Whether a user was EVER established in this page load — the boot-vs-mid-
  // session discriminator for expiry handling. Boot-time expiry (no user yet,
  // so nothing unsaved to lose) auto-redirects to Entra; mid-session expiry
  // keeps the modal so in-progress work isn't ripped away by a redirect.
  const [hadUser, setHadUser] = useState(false)
  const [autoLoginInFlight, setAutoLoginInFlight] = useState(false)
  useEffect(() => {
    userRef.current = user
    if (user) {
      setHadUser(true)
      // A real session exists again — re-arm the boot auto-login for the next
      // genuine expiry in this tab.
      sessionStorage.removeItem(AUTO_LOGIN_GUARD_KEY)
    }
  }, [user])

  // Boot-time expiry → straight to Entra login instead of the modal. No
  // prompt: the SSO cookie completes the round-trip silently in the common
  // case. One attempt per tab session (guard) so a bounce-back that stays
  // expired falls through to the modal instead of looping.
  const willAutoLoginOnBoot =
    msalSettled && isSessionExpired && !hadUser && !devPersona && !sessionStorage.getItem(AUTO_LOGIN_GUARD_KEY)
  useEffect(() => {
    if (!willAutoLoginOnBoot) return
    sessionStorage.setItem(AUTO_LOGIN_GUARD_KEY, String(Date.now()))
    setAutoLoginInFlight(true)
    const returnUrl = `${location.pathname}${location.search}`
    if (location.pathname !== '/' && !location.pathname.startsWith('/auth/callback')) {
      localStorage.setItem('returnUrl', returnUrl)
    }
    console.warn('🔁 Boot-time session expiry — redirecting to Entra login (silent SSO)')
    sessionManager.reset()
    instance
      .loginRedirect({ ...autoLoginRequest, state: returnUrl || undefined })
      .catch((e) => {
        // interaction_in_progress or config errors: fall back to the modal.
        console.error('Boot-time auto-login failed — falling back to the session modal:', e)
        sessionManager.markExpired()
        setAutoLoginInFlight(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [willAutoLoginOnBoot, instance, location.pathname, location.search])

  // Watchdog for the auto-login redirect: while it is "in flight" the session
  // modal is suppressed AND sessionManager was reset, so if the loginRedirect
  // never navigates (blocked navigation, Entra silently failing) the user is
  // left with a dead app and no recovery affordance. A normal redirect
  // navigates away well within the window, killing this timer with the page.
  useEffect(() => {
    if (!autoLoginInFlight) return
    const watchdog = setTimeout(() => {
      if (userRef.current) {
        // Session recovered by other means — just lift the suppression.
        setAutoLoginInFlight(false)
        return
      }
      console.error(
        `❌ Boot auto-login redirect did not navigate within ${AUTO_LOGIN_WATCHDOG_MS}ms — surfacing session modal`
      )
      sessionManager.markExpired()
      setAutoLoginInFlight(false)
    }, AUTO_LOGIN_WATCHDOG_MS)
    return () => clearTimeout(watchdog)
  }, [autoLoginInFlight])

  const isDevelopmentMode = import.meta.env.MODE === 'development' || import.meta.env.VITE_ENABLE_TEST_USERS === 'true'

  // Restore test persona from localStorage on mount (development mode only)
  useEffect(() => {
    if (isDevelopmentMode) {
      try {
        const savedPersona = localStorage.getItem('dev_selected_persona')
        if (savedPersona) {
          const persona = JSON.parse(savedPersona) as TestPersona
          if (import.meta.env.DEV) console.log('✅ Restored test persona from localStorage:', persona.personnelNumber, 'managerPersonnelNumber:', persona.managerPersonnelNumber)
          setDevPersona(persona)
        }
      } catch (error) {
        console.error('Failed to restore dev persona from localStorage:', error)
        localStorage.removeItem('dev_selected_persona')
      } finally {
        setPersonaLoaded(true)
      }
    } else {
      setPersonaLoaded(true)
    }
  }, [isDevelopmentMode])

  // Acquire access token for API calls after MSAL login
  useEffect(() => {
    const acquireToken = async () => {
      // Prevent concurrent token acquisition
      if (isAcquiringToken) {
        if (import.meta.env.DEV) console.log('⏭️ Skipping token acquisition - already in progress')
        return
      }

      if (account && instance) {
        setIsAcquiringToken(true)
        try {
          // Get the ID token (what MSAL provides by default)
          let response = await instance.acquireTokenSilent({
            ...silentRequest,
            account: account,
          })
          // MSAL renews based on the ACCESS token's expiry, so it can hand
          // back a cached idToken that is itself already expired (overnight
          // tab). Storing it would 401 every request — force a real renewal.
          const idTokenExp = decodeJwtExp(response.idToken)
          if (idTokenExp !== null && idTokenExp * 1000 <= Date.now()) {
            console.warn('⚠️ Cached idToken is expired — forcing token renewal')
            response = await instance.acquireTokenSilent({
              ...silentRequest,
              account: account,
              forceRefresh: true,
            })
          }
          // For now, use the ID token as the access token
          // The backend will need to accept ID tokens
          localStorage.setItem('access_token', response.idToken)
          // Release any requests held by the boot readiness gate
          signalAuthTokenReady()
          if (import.meta.env.DEV) console.log('✅ ID token acquired and stored')
          
          // Debug: Decode and log token claims to verify configuration
          try {
            const tokenParts = response.idToken.split('.')
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]))
              if (import.meta.env.DEV) console.log('🔐 Token claims:', {
                aud: payload.aud,      // Should match AZURE_AD_CLIENT_ID on backend
                iss: payload.iss,      // Should match backend's expected issuer
                exp: new Date(payload.exp * 1000).toISOString(),
                iat: new Date(payload.iat * 1000).toISOString(),
                name: payload.name,
                preferred_username: payload.preferred_username,
                oid: payload.oid,
              })
              if (import.meta.env.DEV) console.log('🔧 Frontend config:', {
                clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID,
                tenantId: import.meta.env.VITE_AZURE_AD_TENANT_ID,
              })
            }
          } catch (decodeError) {
            console.warn('Could not decode token for debugging:', decodeError)
          }
        } catch (error) {
          console.error('Failed to acquire token silently:', error)

          // Session truly expired: iframe timeout OR Entra explicitly demanding
          // interaction. Clear the stale stored token in both cases — leaving it
          // meant every request fired a known-dead bearer, 401'd, and burned its
          // single retry before the modal appeared.
          if ((error instanceof BrowserAuthError &&
              (error.errorCode === 'monitor_window_timeout' || error.errorCode === 'interaction_required'))
              || error instanceof InteractionRequiredAuthError) {
            console.warn('🔴 Silent token acquisition failed - session expired')
            localStorage.removeItem('access_token')
            sessionManager.markExpired()
          }
        } finally {
          setIsAcquiringToken(false)
        }
      }
    }

    if (account && !isAcquiringToken) {
      acquireToken()
    }
  }, [account, instance]) // Don't include isAcquiringToken to avoid infinite loop

  // Convert MSAL account to our User type
  useEffect(() => {
    // Wait for persona to be loaded from localStorage before initializing user
    if (!personaLoaded) {
      return
    }

    // In development mode, prioritize dev persona if set
    if (isDevelopmentMode && devPersona) {
      if (import.meta.env.DEV) console.log('👤 Using test persona:', devPersona.personnelNumber)
      const testUser: User = {
        id: devPersona.id,
        email: devPersona.email,
        firstName: devPersona.firstName,
        lastName: devPersona.lastName,
        displayName: devPersona.fullName,
        employeeId: devPersona.personnelNumber,
        campus: devPersona.campus,
        department: devPersona.department,
        functionalArea: devPersona.functionalArea,
        jobTitle: devPersona.jobTitle,
        managerId: devPersona.managerPersonnelNumber,
        managerEmployeeId: devPersona.managerPersonnelNumber, // For F&O lookup - same as personnel number for test personas
        managerDisplayName: devPersona.managerPersonnelNumber ? `Manager (${devPersona.managerPersonnelNumber})` : undefined, // Placeholder for test personas - will be enriched by API
        isActive: true,
        roles: devPersona.roles,
        permissions: devPersona.permissions || [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      setUser(testUser)

      // A persona's object in localStorage has no permission grants — those live
      // in the database, keyed to the real user row. Without this, a persona can
      // never see anything they were granted (the backend authorizes them fine,
      // but the UI has nothing to gate on). Fetch the real grants and merge.
      // Best-effort: on failure the persona simply keeps its role-derived view.
      setArePermissionsLoading(true)
      ;(async () => {
        try {
          // With the real account's token: UAT honours a persona only for a signed-in
          // tester, and authenticatedFetch announces a refused persona.
          const res = await authenticatedFetch(`${API_CONFIG.baseURL}/identity/auth/me`, {
            headers: {
              'Content-Type': 'application/json',
              'X-Test-User-Id': devPersona.personnelNumber,
              'X-Test-User-Roles': (devPersona.roles || ['employee']).join(','),
            },
          })
          if (!res.ok) return
          const data = await res.json()
          const granted: string[] = data?.permissions || []
          if (!granted.length) return
          setUser(prev => (prev ? { ...prev, permissions: granted } : prev))
          if (import.meta.env.DEV) console.log('🔑 Persona grants loaded:', granted)
        } catch {
          // Non-fatal — persona keeps whatever its roles allow.
        } finally {
          setArePermissionsLoading(false)
        }
      })()

      // Fetch actual manager name asynchronously for test personas
      if (devPersona.managerPersonnelNumber) {
        import('@/services/api/integrationService').then(({ integrationService }) => {
          integrationService.getEmployeeByPersonnelNumber(devPersona.managerPersonnelNumber!).then(managerData => {
            if (managerData?.full_name) {
              setUser(prev => prev ? { ...prev, managerDisplayName: managerData.full_name } : prev)
              if (import.meta.env.DEV) console.log('✅ Manager name resolved:', managerData.full_name)
            }
          }).catch(err => {
            if (import.meta.env.DEV) console.warn('Could not fetch manager details:', err)
          })
        })
      }
      return
    }

    // Otherwise fetch user data from API (real users)
    if (account) {
      if (import.meta.env.DEV) console.log('👤 Using real user account')
      // Only dispatch personaChanged when switching FROM test persona TO real user
      // (not on every initial load — that causes a redundant onboarding re-check that can timeout)
      if (user && user.employeeId?.startsWith('TEST')) {
        window.dispatchEvent(new Event('personaChanged'))
      }
      fetchUserFromAPI()
    } else {
      setUser(null)
      loadedProfileRef.current = null
      setProfileIncomplete(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchUserFromAPI is stable via useCallback, adding it causes unnecessary re-fetches
  }, [account, devPersona, isDevelopmentMode, personaLoaded])

  // Helper to parse user data from API response
  const parseUserData = useCallback((userData: Record<string, unknown>): User => {
    const employeeId = (userData.employee_id as string) || (userData.employeeId as string) || ''
    // Cache the last known-good personnel number per Azure account so MSAL fallback
    // can use it instead of the Azure OID (which would break permission checks
    // that compare against personnel-number-keyed records like evidence.submitted_by).
    if (employeeId && account) {
      try {
        const key = `eduvos:lastGoodEmployeeId:${account.homeAccountId || account.localAccountId}`
        localStorage.setItem(key, employeeId)
      } catch {
        // localStorage may be disabled — non-fatal
      }
    }
    return {
    id: (userData.id as string) || '',
    email: (userData.email as string) || '',
    firstName: (userData.first_name as string) || (userData.firstName as string) || '',
    lastName: (userData.last_name as string) || (userData.lastName as string) || '',
    displayName: (userData.display_name as string) || (userData.displayName as string) || '',
    employeeId,
    campus: (userData.campus as string) || '',
    officeLocation: (userData.office_location as string) || (userData.officeLocation as string) || '',
    department: (userData.department as string) || '',
    functionalArea: (userData.functional_area as string) || (userData.functionalArea as string) || '',
    positionType: (userData.position_type as string) || (userData.positionType as string) || '',
    faculty: (userData.faculty as string) || '',
    jobTitle: (userData.job_title as string) || (userData.jobTitle as string) || '',
    managerId: (userData.manager_id as string) || (userData.managerId as string),
    managerEmployeeId: (userData.manager_employee_id as string) || (userData.managerEmployeeId as string),
    managerDisplayName: (userData.manager_display_name as string) || (userData.managerDisplayName as string),
    isActive: userData.is_active !== undefined ? (userData.is_active as boolean) : true,
    roles: (userData.roles as string[]) || [],
    permissions: (userData.permissions as string[]) || [],
    inReviewCycle: typeof userData.in_review_cycle === 'boolean' ? (userData.in_review_cycle as boolean) : undefined,
    createdAt: (userData.created_at as string) || (userData.createdAt as string) || new Date().toISOString(),
    updatedAt: (userData.updated_at as string) || (userData.updatedAt as string) || new Date().toISOString(),
    }
  }, [account])

  // Fallback to MSAL account data when API fails
  const fallbackToMSALUser = useCallback(() => {
    if (account) {
      const claims = account.idTokenClaims || {}
      const firstName = (claims.given_name as string) || ''
      const lastName = (claims.family_name as string) || ''

      // CRITICAL: Use cached last-known-good personnel number, NOT claims.oid.
      // claims.oid is the Azure GUID; the rest of the system keys records on
      // personnel number (e.g. evidence.submitted_by). Using OID here would make
      // the user appear as a different identity, hiding edit/delete buttons on
      // their own evidence and breaking other personnel-number-keyed UI.
      let cachedEmployeeId = ''
      try {
        const key = `eduvos:lastGoodEmployeeId:${account.homeAccountId || account.localAccountId}`
        cachedEmployeeId = localStorage.getItem(key) || ''
      } catch {
        // localStorage may be disabled — non-fatal
      }

      const msalUser: User = {
        id: account.homeAccountId || account.localAccountId || '',
        email: account.username || '',
        firstName,
        lastName,
        displayName: account.name || `${firstName} ${lastName}`.trim(),
        employeeId: cachedEmployeeId,
        jobTitle: (claims.jobTitle as string) || '',
        isActive: true,
        roles: [],
        // MSAL-only fallback: /auth/me hasn't answered, so no grants are known yet.
        permissions: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      if (import.meta.env.DEV) {
        console.log('⚠️ Using MSAL fallback user data', cachedEmployeeId
          ? `(cached employeeId: ${cachedEmployeeId})`
          : '(no cached employeeId — permissions may be limited until /auth/me succeeds)')
      }
      setUser(msalUser)
      return true
    }
    return false
  }, [account])

  // Bounded wait for MSAL initialize()/handleRedirectPromise to settle —
  // the boot request flurry can trigger a refresh before inProgress reaches
  // None, when getAllAccounts() still returns [] for a cached account.
  const waitForMsalSettled = useCallback(async (maxWaitMs: number): Promise<boolean> => {
    const start = Date.now()
    while (!msalSettledRef.current && Date.now() - start < maxWaitMs) {
      await new Promise((resolve) => setTimeout(resolve, 100))
    }
    return msalSettledRef.current
  }, [])

  // Attempt to refresh the token with rate limiting and retry protection.
  // SINGLE-FLIGHT: concurrent callers (each 401 handler, fetchUserFromAPI)
  // all await the SAME in-flight refresh. The previous behaviour handed
  // late-comers the CURRENT token — during an expiry that token was dead, so
  // they burned their one retry on it and cascaded into markExpired.
  const attemptTokenRefresh = useCallback(async (): Promise<string | null> => {
    if (refreshInFlightRef.current) {
      if (import.meta.env.DEV) console.log('⏭️ Token refresh already in progress - awaiting shared result')
      return refreshInFlightRef.current
    }

    const doRefresh = async (): Promise<string | null> => {
      const now = Date.now()

      // Cooldown: a refresh just completed — hand back whatever it produced
      // (fresh token on success, null/cleared on failure)
      if (now - lastRefreshTimeRef.current < REFRESH_COOLDOWN_MS) {
        if (import.meta.env.DEV) console.log('⏭️ Token refresh on cooldown - returning existing token')
        return localStorage.getItem('access_token')
      }

      // Check max attempts
      if (refreshAttemptsRef.current >= MAX_REFRESH_ATTEMPTS) {
        console.error(`❌ Max refresh attempts (${MAX_REFRESH_ATTEMPTS}) exceeded`)
        return null
      }

      lastRefreshTimeRef.current = now
      refreshAttemptsRef.current++

      try {
        // Before MSAL settles (initialize / redirect processing), an empty
        // account list is MEANINGLESS — the cached account just isn't visible
        // yet. Wait for the settle instead of failing: returning null here
        // reads as "refresh failed" to the api clients, which then mark the
        // session expired and reject the whole queued boot wave.
        if (!msalSettledRef.current) {
          console.warn('⚠️ Token refresh requested before MSAL settled — waiting for MSAL')
          const settled = await waitForMsalSettled(MSAL_SETTLE_WAIT_MS)
          if (!settled) {
            console.error(`❌ MSAL did not settle within ${MSAL_SETTLE_WAIT_MS}ms — cannot refresh`)
            return null
          }
        }

        const accounts = instance.getAllAccounts()
        if (accounts.length === 0) {
          // MSAL is settled and there is truly no signed-in account: the stored
          // token is unrenewable garbage. Clear it so subsequent requests/boots
          // don't keep replaying a dead token, and mark expired so the modal
          // offers Sign In (the only way forward).
          console.warn('⚠️ No MSAL accounts available for token refresh — clearing stale token')
          localStorage.removeItem('access_token')
          sessionManager.markExpired()
          return null
        }

        const tokenRequest = {
          ...silentRequest,
          account: accounts[0],
          forceRefresh: true, // Force a network request to get a fresh token
        }

        if (import.meta.env.DEV) console.log(`🔄 Attempting silent token refresh (attempt ${refreshAttemptsRef.current}/${MAX_REFRESH_ATTEMPTS})...`)
        sessionManager.markRefreshing()
        const silentResponse = await instance.acquireTokenSilent(tokenRequest)

        // Debug: Log token info to understand what we're getting
        if (import.meta.env.DEV) console.log('🔐 Token response:', {
          hasIdToken: !!silentResponse.idToken,
          hasAccessToken: !!silentResponse.accessToken,
          expiresOn: silentResponse.expiresOn,
          scopes: silentResponse.scopes,
          idTokenPrefix: silentResponse.idToken?.substring(0, 50),
          accessTokenPrefix: silentResponse.accessToken?.substring(0, 50),
        })

        // Store the new token with CONSISTENT key
        // Using idToken since backend validates with audience=clientId (which is in id tokens)
        localStorage.setItem('access_token', silentResponse.idToken)
        // Release any requests held by the boot readiness gate
        signalAuthTokenReady()
        if (import.meta.env.DEV) console.log('✅ Token refreshed successfully')

        // Reset attempts on success and mark session as active
        refreshAttemptsRef.current = 0
        sessionManager.markActive()

        return silentResponse.idToken
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError)

        // Check if this is an iframe timeout error (common when session truly expired)
        const isBrowserAuthError = refreshError instanceof BrowserAuthError
        const isIframeTimeout = isBrowserAuthError &&
          ((refreshError as BrowserAuthError).errorCode === 'monitor_window_timeout' ||
           (refreshError as BrowserAuthError).errorCode === 'interaction_required')

        // Handle InteractionRequiredAuthError or iframe timeout - mark session expired
        if (refreshError instanceof InteractionRequiredAuthError || isIframeTimeout) {
          console.warn('🔴 Session expired - marking session as expired')
          // Clear stored tokens to prevent stale token usage
          localStorage.removeItem('access_token')
          // Mark session as expired - this will trigger the SessionExpiredModal
          sessionManager.markExpired()
        }

        return null
      }
    }

    const inFlight = doRefresh().finally(() => {
      refreshInFlightRef.current = null
    })
    refreshInFlightRef.current = inFlight
    return inFlight
  }, [instance, waitForMsalSettled])

  // The profile loaded: it is the one to keep, and the note goes.
  const profileLoaded = useCallback((userData: Record<string, unknown>) => {
    const loaded = parseUserData(userData)
    loadedProfileRef.current = loaded
    setUser(loaded)
    setProfileIncomplete(false)
  }, [parseUserData])

  // The profile did not load. One that loaded earlier stays - a failed refresh must never take
  // someone's roles away mid-visit. Otherwise the role-less stand-in, with the note and the
  // retry until the profile loads.
  const profileFailed = useCallback(() => {
    if (loadedProfileRef.current) {
      setUser(loadedProfileRef.current)
      return
    }
    setProfileIncomplete(fallbackToMSALUser())
  }, [fallbackToMSALUser])

  // Fetch user data from /auth/me endpoint
  const fetchUserFromAPI = useCallback(async () => {
    try {
      // Get access token from localStorage
      const token = localStorage.getItem('access_token')
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
      
      if (import.meta.env.DEV) console.log('🔍 Fetching user from /auth/me...')
      // Bounded: a hanging /auth/me (saturated gateway) must NOT leave the
      // header userless indefinitely — abort and fall back to MSAL claims.
      const response = await fetch(`${API_CONFIG.baseURL}/identity/auth/me`, {
        headers,
        signal: AbortSignal.timeout(15000),
      })

      if (!response.ok) {
        console.error(`❌ /auth/me failed with status ${response.status}`)
        
        // Handle token expiration gracefully
        if (response.status === 401) {
          console.warn('🔄 Token expired - attempting to acquire fresh token...')
          
          const newToken = await attemptTokenRefresh()
          
          if (newToken) {
            // Retry the request with fresh token
            if (import.meta.env.DEV) console.log('🔄 Retrying /auth/me with fresh token...')
            const retryResponse = await fetch(`${API_CONFIG.baseURL}/identity/auth/me`, {
              headers: {
                ...headers,
                Authorization: `Bearer ${newToken}`,
              },
              signal: AbortSignal.timeout(15000),
            })
            
            if (retryResponse.ok) {
              const userData = await retryResponse.json()
              if (import.meta.env.DEV) console.log('✅ /auth/me succeeded with fresh token')
              profileLoaded(userData)
              return
            } else {
              console.error(`❌ Retry failed with status ${retryResponse.status}`)
            }
          }

          // If we get here, refresh failed - use MSAL fallback instead of redirecting
          console.warn('⚠️ Token refresh unsuccessful - using MSAL fallback')
          profileFailed()
          return
        }
        
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const userData = await response.json()
      if (import.meta.env.DEV) console.log('✅ /auth/me response:', userData)
      
      // Reset refresh attempts on successful API call
      refreshAttemptsRef.current = 0

      profileLoaded(userData)
    } catch (error) {
      console.error('Failed to fetch user from API:', error)
      // Fallback to basic MSAL data if API fails
      profileFailed()
    }
  }, [attemptTokenRefresh, profileFailed, profileLoaded])

  // Ask again while the profile has not loaded - one try at a time, never for a test persona.
  // Not while the session has expired: that needs a sign-in first (the session modal's job),
  // and each try would spend one of the token refreshes every other request shares.
  const retryProfile = useCallback(() => {
    if (!account || devPersona || profileFetchInFlightRef.current) return
    profileFetchInFlightRef.current = true
    fetchUserFromAPI().finally(() => {
      profileFetchInFlightRef.current = false
    })
  }, [account, devPersona, fetchUserFromAPI])
  useProfileRetry(profileIncomplete && !!account && !devPersona && !isSessionExpired, retryProfile)

  const login = async () => {
    // Prevent multiple simultaneous login attempts
    if (isLoading) {
      if (import.meta.env.DEV) console.log('⏭️ Login already in progress')
      return
    }

    try {
      setIsLoading(true)
      // Reset session state before attempting login
      sessionManager.reset()
      if (import.meta.env.DEV) console.log('🔐 Starting login redirect...')
      await instance.loginRedirect(loginRequest)
    } catch (error) {
      console.error('Login failed:', error)
      toast.error('Login failed. Please try again.')
      setIsLoading(false)
    }
  }
  
  // Handle page refresh for session expired. Clears only OUR derived token —
  // the MSAL account cache is deliberately KEPT so the reload can silently
  // re-acquire a fresh token from the cached account / Entra SSO cookie.
  // (The old version deleted every msal* key, which guaranteed the reload came
  // back signed-out and made "Refresh Page" a dead end; interaction-required
  // sessions are the Sign In button's job, not this one's.)
  const handleRefreshPage = useCallback(() => {
    localStorage.removeItem('access_token')
    sessionManager.reset()
    if (import.meta.env.DEV) console.log('🔄 Cleared stale token, reloading page...')
    window.location.reload()
  }, [])

  const logout = async () => {
    try {
      setIsLoading(true)
      prepareForLogout()
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      })
      toast.success('Successfully logged out!')
    } catch (error) {
      console.error('Logout failed:', error)
      toast.error('Logout failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const refetchUser = useCallback(() => {
    // Refresh user data from API
    if (account && !devPersona) {
      fetchUserFromAPI()
    }
  }, [account, devPersona, fetchUserFromAPI])

  // Reset refresh attempts when account changes (new login)
  const profileAccountRef = useRef<string | null>(null)
  useEffect(() => {
    if (account) {
      refreshAttemptsRef.current = 0
      lastRefreshTimeRef.current = 0
      // Someone else signed in: the profile kept from before was not theirs.
      const accountId = account.homeAccountId || account.localAccountId || null
      if (profileAccountRef.current && profileAccountRef.current !== accountId) loadedProfileRef.current = null
      profileAccountRef.current = accountId
    }
  }, [account])

  // Listen for token refresh events from apiClient
  useEffect(() => {
    const handleTokenRefreshNeeded = async () => {
      if (import.meta.env.DEV) console.log('📡 AuthProvider: Received tokenRefreshNeeded event')
      const newToken = await attemptTokenRefresh()
      // Signal the apiClient that refresh is complete (success or failure)
      signalTokenRefreshComplete(newToken)
    }

    window.addEventListener('tokenRefreshNeeded', handleTokenRefreshNeeded)
    return () => {
      window.removeEventListener('tokenRefreshNeeded', handleTokenRefreshNeeded)
    }
  }, [attemptTokenRefresh])

  // A persona the backend refuses (api/personaRefusal.ts) would fail every request that
  // follows while the header still shows it as active. Drop it; the user effect above
  // then loads the real account again.
  useEffect(() => {
    if (!isDevelopmentMode) return
    const handlePersonaRefused = (event: Event) => {
      if (!localStorage.getItem('dev_selected_persona')) return
      const refusal = (event as CustomEvent<PersonaRefusal>).detail
      setDevPersona(null)
      localStorage.removeItem('dev_selected_persona')
      window.dispatchEvent(new Event('personaChanged'))
      toast.error(refusal?.message || 'That test persona can no longer be used.', { id: 'persona-refused' })
    }
    window.addEventListener(PERSONA_REFUSED_EVENT, handlePersonaRefused)
    return () => window.removeEventListener(PERSONA_REFUSED_EVENT, handlePersonaRefused)
  }, [isDevelopmentMode])

  const switchToTestPersona = (persona: TestPersona) => {
    if (!isDevelopmentMode) {
      console.warn('Test persona switching only available in development mode')
      return
    }
    
    setDevPersona(persona)
    localStorage.setItem('dev_selected_persona', JSON.stringify(persona))
    
    // Notify other hooks/components that persona changed
    window.dispatchEvent(new Event('personaChanged'))
    
    toast.success(`Switched to ${persona.fullName}`, {
      icon: '👤',
      duration: 2000,
    })
  }

  const clearTestPersona = () => {
    if (!isDevelopmentMode) {
      console.warn('Test persona clearing only available in development mode')
      return
    }
    
    if (import.meta.env.DEV) console.log('🔄 Clearing test persona and refreshing user data...')
    
    // Clear persona state and storage first
    setDevPersona(null)
    localStorage.removeItem('dev_selected_persona')
    
    // Force fetch real user data (bypass the !devPersona check in refetchUser)
    if (account) {
      fetchUserFromAPI()
    }
    
    // Notify other hooks/components that persona changed (triggers onboarding re-check)
    window.dispatchEvent(new Event('personaChanged'))
    
    toast.success('Switched to real profile', {
      icon: '👤',
      duration: 2000,
    })
  }

  const hasActivePersona = isDevelopmentMode && devPersona !== null

  const value: AuthContextType = {
    user,
    isLoading,
    isSessionExpired,
    login,
    logout,
    refetchUser,
    switchToTestPersona,
    clearTestPersona,
    hasActivePersona,
    isDevelopmentMode,
    arePermissionsLoading,
    isProfileIncomplete: profileIncomplete && !!account && !devPersona,
    retryProfile,
  }

  // Don't show session expired modal on public routes (e.g. anonymous feedback)
  const PUBLIC_ROUTES = ['/feedback/respond']
  const isPublicRoute = PUBLIC_ROUTES.some(r => location.pathname.startsWith(r))

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Session Expired Modal — mid-session expiry only (protects unsaved work).
          Boot-time expiry auto-redirects to Entra instead, so the modal is
          suppressed while that redirect is pending/in flight. */}
      {!isPublicRoute && (
        <SessionExpiredModal
          isOpen={isSessionExpired && !willAutoLoginOnBoot && !autoLoginInFlight}
          onRefresh={handleRefreshPage}
          onLogin={login}
        />
      )}
      {!isPublicRoute && value.isProfileIncomplete && !isSessionExpired && <ProfileLoadingNote onRetry={retryProfile} />}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
